import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { EmptyState, ErrorState, LoadingState, SectionScaffold, TablePageShell } from '@/shared/ui';

import { useAttributes, useCatalogLanguages, useDeleteProductFamily, useProductFamilies } from '../../hooks';
import { resolveLocalizedText } from '../../lib/localizedText';
import { useCatalogPermissions } from '../../lib/useCatalogPermissions';
import { useToast } from '../../lib/useToast';
import { ProductFamilyFormDialog } from '../components/ProductFamilyFormDialog';

/** Product families (attribute templates) — user-story-catalog-005. */
export function ProductFamiliesPage() {
  const { t, i18n } = useTranslation('catalog');
  const { canStructureView, canStructureManage } = useCatalogPermissions();
  const { toast, success, error: notifyError, clear } = useToast();

  const [filter, setFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [deletingCode, setDeletingCode] = useState<string | null>(null);

  const familiesQuery = useProductFamilies({ enabled: canStructureView });
  const languagesQuery = useCatalogLanguages({ enabled: canStructureView });
  const attributesQuery = useAttributes({ enabled: canStructureView });
  const deleteMutation = useDeleteProductFamily();

  const allFamilies = useMemo(() => familiesQuery.data ?? [], [familiesQuery.data]);
  const dictionary = useMemo(() => attributesQuery.data ?? [], [attributesQuery.data]);
  // Content-default locale (adr-tenant-005) — preferred over the portal's own UI language when
  // resolving a translated label (see `resolveLocalizedText`'s resolution-order doc).
  const defaultLocale = useMemo(
    () => languagesQuery.data?.find((l) => l.defaultLocale)?.locale,
    [languagesQuery.data],
  );
  const attributeLabelByCode = useMemo(
    () =>
      new Map(
        dictionary.map((attribute) => [
          attribute.code,
          resolveLocalizedText(attribute.translations, 'label', i18n.language, attribute.code, defaultLocale),
        ]),
      ),
    [dictionary, i18n.language, defaultLocale],
  );

  const families = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return allFamilies;
    return allFamilies.filter((family) => {
      const name = resolveLocalizedText(family.translations, 'name', i18n.language, '', defaultLocale);
      return family.code.toLowerCase().includes(needle) || name.toLowerCase().includes(needle);
    });
  }, [allFamilies, filter, i18n.language, defaultLocale]);

  // Re-derived from the live query so a mutation performed while the dialog is open (attaching an
  // attribute, editing required/variantAxis/sortOrder) is reflected immediately.
  const editing = editingCode ? (allFamilies.find((f) => f.code === editingCode) ?? null) : null;
  const deleting = deletingCode ? (allFamilies.find((f) => f.code === deletingCode) ?? null) : null;

  const openCreate = () => {
    setEditingCode(null);
    setDialogOpen(true);
  };
  const openEdit = (code: string) => {
    setEditingCode(code);
    setDialogOpen(true);
  };
  const confirmDelete = () => {
    if (!deleting) return;
    const { code } = deleting;
    deleteMutation.mutate(code, {
      onSuccess: () => {
        success(t('productFamilies.delete.success', { code }));
        setDeletingCode(null);
      },
      onError: (e) => {
        notifyError(e.message ?? t('productFamilies.delete.error'));
        setDeletingCode(null);
      },
    });
  };

  return (
    <SectionScaffold
      title={t('productFamilies.title')}
      subtitle={t('productFamilies.subtitle')}
      canView={canStructureView}
      settingsTo="/settings/catalog"
      settingsLabel={t('productFamilies.settingsLabel')}
      search={{ value: filter, onChange: setFilter, placeholder: t('productFamilies.searchPlaceholder') }}
      primaryAction={
        canStructureManage ? (
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>
            {t('productFamilies.actions.add')}
          </Button>
        ) : undefined
      }
    >
      <TablePageShell>
        {familiesQuery.isLoading ? (
          <LoadingState label={t('productFamilies.loading')} />
        ) : familiesQuery.isError ? (
          <ErrorState
            title={t('productFamilies.loadError.title')}
            message={familiesQuery.error?.message ?? t('common:error.unknown')}
            onRetry={() => void familiesQuery.refetch()}
          />
        ) : families.length === 0 ? (
          <EmptyState
            title={filter ? t('common:empty.noResults') : t('productFamilies.empty.title')}
            description={
              filter ? t('productFamilies.empty.filteredDescription') : t('productFamilies.empty.description')
            }
            icon={<AccountTreeRoundedIcon />}
            action={
              canStructureManage && !filter ? (
                <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>
                  {t('productFamilies.actions.add')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('productFamilies.table.name')}</TableCell>
                  <TableCell>{t('productFamilies.table.code')}</TableCell>
                  <TableCell align="right">{t('productFamilies.table.attributeCount')}</TableCell>
                  <TableCell>{t('productFamilies.table.variantAxis')}</TableCell>
                  <TableCell>{t('productFamilies.table.status')}</TableCell>
                  <TableCell align="right">{t('productFamilies.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {families.map((family) => {
                  const axisAttributes = family.attributes.filter((a) => a.variantAxis);
                  return (
                    <TableRow key={family.code} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {resolveLocalizedText(
                            family.translations,
                            'name',
                            i18n.language,
                            family.code,
                            defaultLocale,
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          component="code"
                          sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13 }}
                        >
                          {family.code}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{family.attributes.length}</TableCell>
                      <TableCell>
                        {axisAttributes.length > 0 ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            color="primary"
                            label={t('productFamilies.axisChipLabel', {
                              axes: axisAttributes
                                .map((a) => attributeLabelByCode.get(a.attributeCode) ?? a.attributeCode)
                                .join(', '),
                            })}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t('productFamilies.noAxis')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={family.active ? 'success' : 'default'}
                          label={
                            family.active
                              ? t('productFamilies.statusActive')
                              : t('productFamilies.statusInactive')
                          }
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip
                          title={
                            canStructureManage
                              ? t('productFamilies.rowActions.edit')
                              : t('productFamilies.rowActions.view')
                          }
                        >
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label={t('productFamilies.rowActions.editAria', { code: family.code })}
                            onClick={() => openEdit(family.code)}
                          >
                            <EditRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canStructureManage && (
                          <Tooltip title={t('productFamilies.rowActions.delete')}>
                            <IconButton
                              size="small"
                              color="error"
                              aria-label={t('productFamilies.rowActions.deleteAria', { code: family.code })}
                              onClick={() => setDeletingCode(family.code)}
                            >
                              <DeleteOutlineRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TablePageShell>

      <Dialog open={deletingCode !== null} onClose={() => setDeletingCode(null)}>
        <DialogTitle>{t('productFamilies.delete.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('productFamilies.delete.body', { code: deleting?.code })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingCode(null)}>{t('common:action.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={deleteMutation.isPending}
          >
            {t('common:action.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <ProductFamilyFormDialog
        open={dialogOpen}
        editing={editing}
        languages={languagesQuery.data ?? []}
        dictionary={dictionary}
        canManage={canStructureManage}
        onClose={() => setDialogOpen(false)}
        onResult={(severity, message) =>
          severity === 'success' ? success(message) : notifyError(message)
        }
      />

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={clear}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={clear}
          severity={toast?.severity ?? 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </SectionScaffold>
  );
}
